import { Link } from "react-router-dom";
import {
  ArrowRight,
  Building2,
  Trophy,
  Briefcase,
  MapPin,
  Plane,
  Sparkles,
} from "lucide-react";
import Seo from "@/components/Seo";
import { DESTINATION_IMAGES } from "@/lib/media";

const BREADCRUMB = [
  { name: "Home", path: "/" },
  { name: "Bali DMC", path: "/bali-dmc" },
];

const SERVICES = [
  {
    icon: Trophy,
    title: "MICE",
    copy:
      "Bali is one of Asia's premier MICE destinations, combining world-class resort infrastructure with unparalleled cultural experiences. Our Bali MICE desk specialises in incentive travel, product launches, leadership retreats, and large-scale conferences across Nusa Dua, Jimbaran, and Seminyak. We work closely with flagship properties including The Apurva Kempinski, Conrad Bali, Sofitel, and St. Regis to deliver competitive group rates and exclusive venue access. Our MICE packages include venue scouting, themed gala evenings, Balinese cultural performances, teambuilding activations, and seamless airport-to-resort group logistics.",
  },
  {
    icon: Briefcase,
    title: "Group Leisure",
    copy:
      "Bali's diversity makes it ideal for every group profile — from honeymooners and family groups to corporate leisure and pilgrimage tours. We design group leisure programmes spanning Ubud's rice terraces and art villages, Tanah Lot and Uluwatu temple sunset ceremonies, Seminyak and Kuta beach leisure, and white-water rafting on the Ayung River. Programmes are available in 5N/6D to 8N/9D formats, with options for Nusa Penida island extensions, Mount Batur sunrise treks, and Lembongan snorkelling day trips. All programmes include guided sightseeing, comfortable hotels, daily breakfast, and group transfers.",
  },
  {
    icon: Sparkles,
    title: "FIT",
    copy:
      "Bali's magnetic appeal to independent travellers is unmatched — and our FIT desk crafts itineraries that go beyond the standard Kuta-Ubud loop. Our Bali FIT programmes are personalised around the traveller's style: wellness and yoga retreats in Ubud, surf and beach itineraries in Canggu, luxury private villa stays in Seminyak, or spiritual and cultural immersions across the island's lesser-known temples and villages. Every FIT booking includes private airport transfers, hand-picked accommodation, customised day tours, and a dedicated local contact throughout the stay.",
  },
];

const HIGHLIGHTS = [
  "Ubud cultural immersions",
  "Nusa Dua MICE resorts",
  "Mount Batur sunrise treks",
  "Private villa buyouts",
  "Seminyak beach galas",
  "Balinese wellness programmes",
];

const VENUES = [
  { name: "The Apurva Kempinski Bali", note: "Cliff-top Nusa Dua flagship — large-scale MICE specialist" },
  { name: "Conrad Bali", note: "Beachfront pillarless ballroom; private cove for galas" },
  { name: "St. Regis Bali", note: "Lagoon villas + private beach for incentive groups" },
  { name: "Sofitel Bali Nusa Dua Beach Resort", note: "415 rooms, dedicated MICE wing, expansive lawns" },
  { name: "Four Seasons Sayan, Ubud", note: "Rice-paddy retreat for executive offsites" },
  { name: "Bali International Convention Centre (BICC)", note: "Nusa Dua's flagship convention venue" },
];

const FAQS = [
  {
    q: "Why is Bali so popular for incentive travel?",
    a: "Bali combines world-class 5-star resort inventory at competitive rates with deeply photogenic culture and dramatic natural settings. A delegate can attend a strategic session in the morning, take a private blessing ceremony at lunch, and finish with a beachfront gala under the stars — all in one day. Few destinations layer venue, culture, and content as efficiently as Bali.",
  },
  {
    q: "Can Trawelldmc handle large groups in Bali?",
    a: "Yes. Bali holds significant inventory of 350+ key resorts (Apurva Kempinski, Sofitel, Conrad). We routinely operate single-property buyouts for 600-pax groups and split-property programmes for 2,000+ pax. We also coordinate group visa pre-clearance, charter flights, and pre/post-tour extensions to Lombok and Java.",
  },
  {
    q: "What's the ideal time to visit Bali?",
    a: "May to September is dry, sunny, and ideal for outdoor galas and beach programmes. October to April brings warm humid weather with afternoon rain showers — still highly workable, especially for indoor MICE programmes and wellness retreats.",
  },
];

const FAQ_JSONLD = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQS.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};

export default function BaliDmc() {
  return (
    <div data-testid="bali-dmc-page">
      <Seo
        path="/bali-dmc"
        title="Bali DMC | MICE, Incentives & Group Travel | Trawelldmc"
        description="DMC in Bali — Nusa Dua, Ubud, Seminyak. MICE, incentive, group leisure & FIT programmes built end-to-end. Request a Bali proposal."
        breadcrumb={BREADCRUMB}
        extraJsonLd={FAQ_JSONLD}
      />

      <section className="relative">
        <img
          src={DESTINATION_IMAGES.baliId}
          alt="Bali incentive travel — Trawelldmc DMC Indonesia"
          width="1920"
          height="1080"
          loading="eager"
          fetchPriority="high"
          decoding="async"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(90deg, rgba(11,25,44,0.92) 0%, rgba(11,25,44,0.55) 100%)",
          }}
        />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-32 md:py-44 text-white">
          <div className="max-w-3xl">
            <div className="overline !text-[#FF5238] mb-5">
              Bali DMC · Indonesia · Nusa Dua · Ubud · Seminyak
            </div>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-light tracking-tight leading-[1.06]">
              <span className="text-[#FF5238]">Bali</span> Destination Management Company (DMC) & MICE Specialist
            </h1>
            <p className="text-white/80 mt-6 text-sm tracking-[0.2em] uppercase font-medium">
              Island of Gods, MICE & Beyond
            </p>
            <p className="text-white/80 mt-6 text-lg max-w-2xl leading-relaxed">
              MICE, incentive, group leisure and FIT programmes across Bali —
              engineered end-to-end by our on-ground Indonesia team.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link
                to="/inquiry?destination=Bali"
                className="btn-primary"
                data-testid="bali-cta-proposal"
              >
                Get a Bali proposal <ArrowRight className="h-4 w-4" />
              </Link>
              <Link to="/quote" className="btn-secondary" data-testid="bali-cta-ai-quote">
                Instant AI estimate
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* WHY BALI / OVERVIEW */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="overline mb-4">Why Bali</div>
            <h2 className="font-heading text-4xl md:text-5xl font-light tracking-tight text-[#0B192C]">
              Culture, resorts, <br />and content that travels.
            </h2>
          </div>
          <div className="lg:col-span-7 space-y-6 text-slate-700 text-lg leading-relaxed">
            <p>
              Bali is the ultimate convergence of natural beauty, spiritual
              culture, and world-class MICE infrastructure. Trawelldmc's Bali
              desk specialises in incentive travel, corporate retreats, and
              group leisure across Nusa Dua, Ubud, Seminyak, and beyond. From
              private villa buyouts and beachfront gala evenings to Balinese
              wellness programmes and adventure team-building, Bali offers an
              unmatched variety of experiences that leave lasting impressions.
            </p>
            <p>
              With a robust network of 5-star resorts, DMC ground resources,
              and local expertise, we deliver Bali beyond the brochure — the
              version your delegates remember years after they fly home.
            </p>
          </div>
        </div>
      </section>

      {/* HIGHLIGHTS */}
      <section className="bg-[#FDFCF8] border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-32">
          <div className="mb-16 max-w-2xl">
            <div className="overline mb-4">Top Experiences</div>
            <h2 className="font-heading text-4xl md:text-5xl font-light tracking-tight text-[#0B192C]">
              Bali highlights.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {HIGHLIGHTS.map((h) => (
              <div key={h} className="card-flat p-6 flex items-start gap-3">
                <MapPin className="h-5 w-5 text-[#FF5238] flex-shrink-0 mt-0.5" />
                <span className="text-[#0B192C] font-medium">{h}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-32">
        <div className="mb-16 max-w-2xl">
          <div className="overline mb-4">What we deliver</div>
          <h2 className="font-heading text-4xl md:text-5xl font-light tracking-tight text-[#0B192C]">
            Our Bali services.
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {SERVICES.map((s) => (
            <div key={s.title} className="card-flat p-8" data-testid={`bali-service-${s.title.toLowerCase().replace(/ /g, "-")}`}>
              <s.icon className="h-8 w-8 text-[#FF5238]" />
              <h3 className="font-heading text-xl font-medium tracking-tight text-[#0B192C] mt-5">
                {s.title}
              </h3>
              <p className="text-slate-600 mt-3 leading-relaxed text-sm">{s.copy}</p>
            </div>
          ))}
        </div>
      </section>

      {/* VENUES */}
      <section className="bg-[#0B192C] text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-32">
          <div className="mb-16 max-w-2xl">
            <div className="overline !text-[#FF5238] mb-4">Top venues</div>
            <h2 className="font-heading text-4xl md:text-5xl font-light tracking-tight">
              Hotels & venues <br />
              <span className="text-[#FF5238]">we partner with.</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-6">
            {VENUES.map((v) => (
              <div key={v.name} className="border-t border-white/15 pt-5">
                <div className="flex gap-3 items-start">
                  <Building2 className="h-5 w-5 text-[#FF5238] flex-shrink-0 mt-1" />
                  <div>
                    <div className="font-heading text-lg font-medium">{v.name}</div>
                    <div className="text-sm text-white/60 mt-1">{v.note}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-32">
        <div className="mb-16 max-w-2xl">
          <div className="overline mb-4">FAQ</div>
          <h2 className="font-heading text-4xl md:text-5xl font-light tracking-tight text-[#0B192C]">
            Frequently Asked Questions
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-10">
          {FAQS.map((f, i) => (
            <div key={f.q} data-testid={`bali-faq-${i}`}>
              <h3 className="font-heading text-xl font-medium text-[#0B192C] tracking-tight">
                {f.q}
              </h3>
              <p className="text-slate-600 mt-3 leading-relaxed">{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#FDFCF8] border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-32 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <div className="overline mb-4">Next step</div>
            <h2 className="font-heading text-4xl md:text-5xl font-light tracking-tight text-[#0B192C]">
              Ready to plan your <br />
              <span className="text-[#FF5238]">Bali programme?</span>
            </h2>
            <p className="text-slate-600 mt-6 max-w-xl text-lg leading-relaxed">
              Contact Trawelldmc for a custom Bali proposal. We respond within
              24 hours.
            </p>
          </div>
          <div className="lg:col-span-5 flex flex-col gap-4">
            <Link
              to="/inquiry?destination=Bali"
              className="btn-primary w-full justify-center"
              data-testid="bali-bottom-cta"
            >
              Request a proposal <ArrowRight className="h-4 w-4" />
            </Link>
            <Link to="/contact" className="btn-secondary w-full justify-center">
              Contact our team
            </Link>
          </div>
        </div>
      </section>

      {/* YOU MAY ALSO LIKE */}
      <section>
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-16">
          <div className="overline mb-6">You may also like</div>
          <nav
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
            aria-label="Related DMC destination pages"
          >
            <Link to="/singapore-dmc" className="group flex items-start gap-3" data-testid="bali-link-singapore">
              <MapPin className="h-5 w-5 text-[#FF5238] mt-0.5" />
              <div>
                <div className="font-medium text-[#0B192C] group-hover:underline">DMC Singapore</div>
                <div className="text-sm text-slate-500 mt-1">MICE capital of Asia</div>
              </div>
            </Link>
            <Link to="/thailand-dmc" className="group flex items-start gap-3" data-testid="bali-link-thailand">
              <MapPin className="h-5 w-5 text-[#FF5238] mt-0.5" />
              <div>
                <div className="font-medium text-[#0B192C] group-hover:underline">DMC Thailand</div>
                <div className="text-sm text-slate-500 mt-1">Bangkok · Phuket · Chiang Mai</div>
              </div>
            </Link>
            <Link to="/philippines-dmc" className="group flex items-start gap-3" data-testid="bali-link-philippines">
              <MapPin className="h-5 w-5 text-[#FF5238] mt-0.5" />
              <div>
                <div className="font-medium text-[#0B192C] group-hover:underline">DMC Philippines</div>
                <div className="text-sm text-slate-500 mt-1">Manila · Cebu · Palawan</div>
              </div>
            </Link>
            <Link to="/" className="group flex items-start gap-3" data-testid="bali-link-home">
              <Plane className="h-5 w-5 text-[#FF5238] mt-0.5" />
              <div>
                <div className="font-medium text-[#0B192C] group-hover:underline">Trawelldmc Home</div>
                <div className="text-sm text-slate-500 mt-1">All destinations & services</div>
              </div>
            </Link>
          </nav>
        </div>
      </section>
    </div>
  );
}
